/**
 * Installation Details List with Infinite Scroll
 *
 * Features:
 * - Infinite scroll with pagination
 * - Filters: From/To Date, InCharge, AMC Month, AMC/Non AMC, Status, Client Name, Area, City, Org Type
 * - All dropdown options are dynamic
 * - Manual fetch with filters
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Button, TextField, MenuItem, Box, Typography, Autocomplete } from '@mui/material';
import { Plus, Search as SearchIcon, BrushCleaning, RefreshCw } from 'lucide-react';
import DataTable from '../common/DataTable/index';
import CommonDialog from '../common/CommonDialog';
import AddDcr from './AddDcr';
import dayjs from 'dayjs';
import { fetchDcrAreaListCombo } from '../../../apis/DCR/Area';
import { fetchDcrCityListCombo } from '../../../apis/DCR/City';
import { fetchDcrList } from '../../../apis/DCR/List';
import Tooltip from '../../../utils/Tooltip';
import DatePicker from '../common/DatePicker/DatePicker';
import {
  FilterFieldSx,
  DropDownSX,
  CSS_PRIMARY,
  PRIMARY_COLOR_HOVER
} from '../../../constants/style';
import { fetchUsersCombo } from '../../../apis/Masters/Users';
import { fetchGlobalTypesCombo } from '../../../apis/Masters/GlobalType';
import { MastersId } from '../../../constants/constant';
import { fetchSoftwareNamesCombo } from '../../../apis/Masters/SoftwareName';
import Search from '../common/Search';
import ExportExcel from '../common/ExportExcel';
import { useResponsive, userScreenAccess, IsAdmins } from '../../../utils';
import { fetchDcrClients } from '../../../apis/DCR/Clients';
import { AuthConstant, localStorageKeyName } from '../../../constants/constant';
import ChooseClient from '../common/ChooseClient';
import { formatDate } from '../../../utils';

const headers = [
  {
    label: 'In Charge',
    key: 'userName',
    maxWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Visiting Date',
    key: 'visitingDateString',
    maxWidth: 120,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Client Name',
    key: 'dailyCallClientName',
    maxWidth: 120,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Area',
    key: 'dailyCallArea',
    maxWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'City',
    key: 'dailyCallCity',
    maxWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Call Type',
    key: 'callType',
    maxWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'User / Non User',
    key: 'softwareUsageType',
    maxWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Software Name',
    key: 'softwareName',
    maxWidth: 120,
    sortable: true,
    resizable: true
  },
  {
    label: 'Remarks',
    key: 'remarks',
    maxWidth: 140,
    sortable: true,
    resizable: true
  },
  {
    label: 'Actions',
    key: '_actions',
    maxWidth: 80,
    sortable: false,
    align: 'center',
    render: (_val, row) => (
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          _editRowHandler?.(row);
        }}
        sx={{
          fontSize: '0.7rem',
          textTransform: 'none',
          borderRadius: 1.5,
          py: 0.3,
          px: 1.2,
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
        }}
      >
        Edit
      </Button>
    )
  }
];

let _editRowHandler = null;

const emptyFilter = {
  dateFrom: formatDate(dayjs()),
  dateTo: formatDate(dayjs()),
  userGUID: 0,
  softwareUsageTypeGUID: 0,
  softwareNameGUID: 0,
  statusGUID: 0,
  clientName: '',
  dailyCallAreaGUID: 0,
  dailyCallCityGUID: 0,
  callTypeGUID: 0,
  dailyCallClientGUID: 0
};

/**
 * InstallationDetailsList Component with Lazy Loading
 *
 * Features:
 * - Infinite scroll with pagination (50 records per batch)
 * - Filters: From/To Date, InCharge, AMC Month, AMC/Non AMC, Status, Client Name, Area, City, Org Type
 * - All dropdown options are dynamic
 * - Manual fetch: Click "Fetch" button to load data with current filters
 * - Add/Edit installation dialogs
 * - Manual refresh capability
 */
const DcrList = () => {
  // Filters state (for UI inputs)
  const [filters, setFilters] = useState(emptyFilter);

  // API filters state (only updated when Fetch button is clicked)
  const [apiFilters, setApiFilters] = useState(emptyFilter);

  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  // Filter options
  const [inChargeOptions, setInChargeOptions] = useState([]);
  const [areaOptions, setAreaOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [callTypeOptions, setCallTypeOptions] = useState([]);
  const [softwareOptions, setSoftwareOptions] = useState([]);
  const [userNonUserOptions, setUserNonUserOptions] = useState([]);
  const [dcrList, setDcrList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [clients, setClients] = useState([]);
  const { isMobile } = useResponsive();
  const isAdmin = IsAdmins();

  const user = localStorage.getItem(localStorageKeyName);
  const userDetails = user ? JSON.parse(user) : null;
  // const isUser = userDetails.role == AuthConstant.USER;

  const userAccess = userScreenAccess();

  // Wire module-level handler for the Actions column
  _editRowHandler = (row) => {
    setSelectedClient(row);
    setOpenDialog(true);
  };

  const handleAddClient = () => {
    if (!userAccess.hasAddPermission) return;
    setSelectedClient(null);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedClient(null);
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleDateRangeChange = (nextRange) => {
    setFilters({
      ...filters,
      dateFrom: nextRange?.startDate ? dayjs(nextRange.startDate) : null,
      dateTo: nextRange?.endDate ? dayjs(nextRange.endDate) : null
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleFetch();
    }
  };

  // Filtered data
  const filteredData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return dcrList;
    return dcrList.filter((row) => {
      // Normalize values: convert boolean isActive → display text so
      // searching "Active" or "Inactive" / "InActive" works correctly
      const normalizedValues = Object.entries(row).map(([key, val]) => {
        if (key === 'isActive')
          return typeof val === 'boolean'
            ? val
              ? 'active'
              : 'inactive'
            : String(val).toLowerCase();
        return String(val).toLowerCase();
      });
      return normalizedValues.some((v) => v.includes(q));
    });
  }, [dcrList, searchQuery]);

  const fetchInstallationListWithPagination = useCallback(
    async (page, pageSize) => {
      try {
        const response = [];
        // Handle different response formats
        let data = [];
        let totalRecords = 0;

        if (Array.isArray(response)) {
          // API returns array directly
          data = response;
          totalRecords = response.length;
        } else if (response?.data) {
          if (Array.isArray(response.data)) {
            data = response.data;
            totalRecords = response.totalRecords || response.data.length;
          } else if (response.data?.data && Array.isArray(response.data.data)) {
            data = response.data.data;
            totalRecords = response.data.totalRecords || response.data.data.length;
          }
        }

        // Return in expected format
        return {
          data: data,
          totalRecords: totalRecords
        };
      } catch (err) {
        console.log('Error fetching installation list with pagination:', err);
        return { data: [], totalRecords: 0 };
      }
    },
    [apiFilters]
  );

  const fetchDcrListResponse = async (filters) => {
    setIsLoading(true);
    try {
      const response = await fetchDcrList(filters);
      setDcrList(response || []);
    } catch (err) {
      console.log('Error fetching installation list:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDependencies = async () => {
    try {
      const [usersRes, areasRes, citiesRes, userNonUserRes, callTypeRes, softwareRes, clientsRes] =
        await Promise.all([
          fetchUsersCombo(),
          fetchDcrAreaListCombo(),
          fetchDcrCityListCombo(),
          fetchGlobalTypesCombo(MastersId.USER_NON_USER),
          fetchGlobalTypesCombo(MastersId.CALL_TYPE),
          fetchSoftwareNamesCombo(),
          fetchDcrClients({ statusGUID: 1 })
        ]);

      const loadedUsers = usersRes || [];
      const loadedAreas = areasRes || [];
      const loadedCities = citiesRes || [];
      const loadedUserNonUser = userNonUserRes || [];
      const loadedCallType = callTypeRes || [];
      const loadedSoftware = softwareRes || [];
      const loadedClients = clientsRes || [];
      // const loadedSoftware = softwareRes.data?.data || softwareRes.data || [];

      setInChargeOptions(loadedUsers);
      setAreaOptions(loadedAreas);
      setCityOptions(loadedCities);
      setUserNonUserOptions(loadedUserNonUser);
      setSoftwareOptions(loadedSoftware);
      setCallTypeOptions(loadedCallType);
      setClients(loadedClients);
    } catch (err) { }
  };

  // Fetch dependencies on mount
  React.useEffect(() => {
    fetchDependencies();
    const payload = {
      ...filters,
      userGUID: userDetails.userGUID ?? 0
    };
    setFilters(payload);
    fetchDcrListResponse(payload);
  }, []);

  /**
   * NEW: Reset infinite scroll when filters change
   * This forces the DataTable to reinitialize with new filters
   */
  const handleResetFilters = () => {
    const payload = {
      ...emptyFilter,
      userGUID: userDetails.userGUID ?? 0
    };
    setFilters(payload);
    fetchDcrListResponse(payload);
    // Note: We don't reset apiFilters or trigger fetch here
  };

  const handleFetch = () => {
    // setApiFilters({ ...filters }); // Copy current UI filters to API filters
    // setFilterResetKey((prev) => prev + 1); // Trigger DataTable reset and fetch
    const payload = {
      ...filters,
      dateFrom: formatDate(filters.dateFrom),
      dateTo: formatDate(filters.dateTo)
    };
    fetchDcrListResponse(payload);
  };

  const handleRefresh = () => {
    fetchDcrListResponse(filters);
  };

  // ── Autocomplete change handler ─────────────────────────────────────────────

  const handleClientChange = (dailyCallClientGUID) => {
    const next = { ...filters, dailyCallClientGUID };
    setFilters(next);
  };

  const handleCityChange = (event, value) => {
    const dailyCallCityGUID = value?.dailyCallCityGUID || 0;
    const next = { ...filters, dailyCallCityGUID };
    setFilters(next);
  };

  const handleAreaChange = (event, value) => {
    const dailyCallAreaGUID = value?.dailyCallAreaGUID || 0;
    const next = { ...filters, dailyCallAreaGUID };
    setFilters(next);
  };

  const handleInChargeChange = (event, value) => {
    const userGUID = value?.userGUID || 0;
    const next = { ...filters, userGUID };
    setFilters(next);
  };

  const handleUserTypeChange = (event, value) => {
    const softwareUsageTypeGUID = value?.globalTypeID || 0;
    const next = { ...filters, softwareUsageTypeGUID };
    setFilters(next);
  };

  const handleCallTypeChange = (event, value) => {
    const callTypeGUID = value?.globalTypeID || 0;
    const next = { ...filters, callTypeGUID };
    setFilters(next);
  };

  const handleSoftwareNameChange = (event, value) => {
    const softwareNameGUID = value?.softwareNameGUID || 0;
    const next = { ...filters, softwareNameGUID };
    setFilters(next);
  };

  const handleStartDateRangeChange = (nextRange) => {
    setFilters({
      ...filters,
      dateFrom: nextRange || null
    });
  };

  const handleEndDateRangeChange = (nextRange) => {
    setFilters({
      ...filters,
      dateTo: nextRange || null
    });
  };

  return (
    <div className="w-100">
      <Box
        sx={{
          display: { xs: 'flex' },
          alignItems: 'center',
          justifyContent: { xs: 'space-between', md: 'flex-end' },
          mb: 3
        }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          color="text.primary"
          sx={{ display: { md: 'none' } }}
        >
          DCR Records
        </Typography>
      </Box>

      <Box
        sx={{
          mb: 3,
          p: 2,
          backgroundColor: '#fff',
          borderRadius: 2,
          boxShadow: '0px 2px 10px rgba(0,0,0,0.04)',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        {/* Filter Fields Grid */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
              lg: 'repeat(5, 1fr)'
            },
            gap: 1,
            mb: 2,
            width: '100%'
          }}
        >
          {/* Date Range Filter */}
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 2', lg: 'span 2' }
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 1,
                minWidth: 0
              }}
            >
              <Box sx={{ width: '100%', minWidth: 0 }}>
                <DatePicker
                  placeholder="Start Date"
                  label="Start Date"
                  value={filters.dateFrom || null}
                  onChange={handleStartDateRangeChange}
                  height={36}
                  width="100%"
                  onKeyDown={handleKeyDown}
                />
              </Box>

              <Box sx={{ width: '100%', minWidth: 0 }}>
                <DatePicker
                  label="End Date"
                  placeholder="End Date"
                  value={filters.dateTo || null}
                  onChange={handleEndDateRangeChange}
                  height={36}
                  width="100%"
                  onKeyDown={handleKeyDown}
                />
              </Box>
            </Box>
          </Box>

          <Autocomplete
            size="small"
            fullWidth
            options={userNonUserOptions}
            value={
              userNonUserOptions.find((u) => u.globalTypeID === filters.softwareUsageTypeGUID) ||
              null
            }
            onChange={handleUserTypeChange}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.globalTypeName || ''}
            isOptionEqualToValue={(option, value) => option?.globalTypeID === value?.globalTypeID}
            onKeyDown={handleKeyDown}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="User / Non User"
                placeholder="Select User / Non User"
                sx={FilterFieldSx}
              />
            )}
          />

          <Autocomplete
            size="small"
            fullWidth
            options={softwareOptions}
            value={
              softwareOptions.find((u) => u.softwareNameGUID === filters.softwareNameGUID) || null
            }
            onChange={handleSoftwareNameChange}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.softwareName || ''}
            isOptionEqualToValue={(option, value) =>
              option?.softwareNameGUID === value?.softwareNameGUID
            }
            onKeyDown={handleKeyDown}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Software"
                placeholder="Select Software"
                sx={FilterFieldSx}
              />
            )}
          />

          <Autocomplete
            size="small"
            fullWidth
            options={callTypeOptions}
            value={callTypeOptions.find((u) => u.globalTypeID === filters.callTypeGUID) || null}
            onChange={handleCallTypeChange}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.globalTypeName || ''}
            isOptionEqualToValue={(option, value) => option?.globalTypeID === value?.globalTypeID}
            onKeyDown={handleKeyDown}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Call Type"
                placeholder="Select Call Type"
                sx={FilterFieldSx}
              />
            )}
          />

          <Box
            sx={{
              width: '100%',
              gridColumn: { xs: 'span 1', sm: 'span 2', md: 'span 2', lg: 'span 2' }
            }}
          >
            {/* <Autocomplete
              size="small"
              fullWidth
              options={clients}
              value={
                clients.find((a) => a.dailyCallClientGUID === filters.dailyCallClientGUID) || null
              }
              onChange={handleClientChange}
              disablePortal={false}
              disableClearable={false}
              getOptionLabel={(option) => option?.dailyCallClientName || ''}
              isOptionEqualToValue={(option, value) =>
                option?.dailyCallClientGUID === value?.dailyCallClientGUID
              }
              onKeyDown={handleKeyDown}
              slotProps={{
                paper: {
                  sx: DropDownSX
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Client Name"
                  placeholder="Select Client"
                  sx={FilterFieldSx}
                />
              )}
            /> */}
            {/* Client Name */}
            <ChooseClient
              clients={clients}
              value={filters.dailyCallClientGUID}
              onKeyDown={handleKeyDown}
              onChange={handleClientChange}
            />
          </Box>

          <Autocomplete
            size="small"
            fullWidth
            options={areaOptions}
            value={
              areaOptions.find((a) => a.dailyCallAreaGUID === filters.dailyCallAreaGUID) || null
            }
            onChange={handleAreaChange}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.dailyCallArea || ''}
            isOptionEqualToValue={(option, value) =>
              option?.dailyCallAreaGUID === value?.dailyCallAreaGUID
            }
            onKeyDown={handleKeyDown}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Area" placeholder="Select Area" sx={FilterFieldSx} />
            )}
          />

          <Autocomplete
            size="small"
            fullWidth
            options={cityOptions}
            value={
              cityOptions.find((c) => c.dailyCallCityGUID === filters.dailyCallCityGUID) || null
            }
            onChange={handleCityChange}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.dailyCallCity || ''}
            isOptionEqualToValue={(option, value) =>
              option?.dailyCallCityGUID === value?.dailyCallCityGUID
            }
            onKeyDown={handleKeyDown}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField {...params} label="City" placeholder="Select City" sx={FilterFieldSx} />
            )}
          />

          <Autocomplete
            size="small"
            fullWidth
            options={inChargeOptions}
            value={inChargeOptions.find((u) => u.userGUID === filters.userGUID) || null}
            onChange={handleInChargeChange}
            disablePortal={false}
            disableClearable={false}
            getOptionLabel={(option) => option?.userName || ''}
            isOptionEqualToValue={(option, value) => option?.userGUID === value?.userGUID}
            readOnly={!isAdmin}
            onKeyDown={handleKeyDown}
            slotProps={{
              paper: {
                sx: DropDownSX
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="In Charge"
                placeholder="Select In Charge"
                sx={FilterFieldSx}
              />
            )}
          />
        </Box>

        {/* Action Buttons Row */}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}
        >
          {/* Fetch Button */}
          <Tooltip text="Fetch">
            <Button
              variant="outlined"
              size="small"
              onClick={handleFetch}
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                whiteSpace: 'nowrap',
                textCapitalize: 'capitalize',
                fontWeight: 500,
                fontSize: '0.8rem',
                py: 0.4,
                px: 1.2,
                color: CSS_PRIMARY, // text color
                borderColor: CSS_PRIMARY, // outline color

                '&:hover': {
                  borderColor: PRIMARY_COLOR_HOVER,
                  color: PRIMARY_COLOR_HOVER
                }
              }}
            >
              <SearchIcon size={14} style={{ marginRight: 4 }} />
              Fetch
            </Button>
          </Tooltip>

          {/* Refresh Button */}
          {/* <Tooltip text="Refresh">
            <Button
              variant="outlined"
              size="small"
              onClick={handleRefresh}
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                whiteSpace: 'nowrap',
                fontWeight: 500,
                fontSize: '0.8rem',
                py: 0.4,
                px: 1.2
              }}
            >
              <RefreshCw size={14} style={{ marginRight: 4 }} />
              Refresh
            </Button>
          </Tooltip> */}

          {/* Reset Button */}
          <Tooltip text="Reset filters">
            <Button
              variant="outlined"
              size="small"
              onClick={handleResetFilters}
              sx={{
                textTransform: 'none',
                borderRadius: 1,
                whiteSpace: 'nowrap',
                fontWeight: 500,
                fontSize: '0.8rem',
                py: 0.4,
                px: 1.2,
                color: CSS_PRIMARY, // text color
                borderColor: CSS_PRIMARY, // outline color

                '&:hover': {
                  borderColor: PRIMARY_COLOR_HOVER,
                  color: PRIMARY_COLOR_HOVER
                }
              }}
            >
              <BrushCleaning size={14} style={{ marginRight: 4 }} />
              Reset
            </Button>
          </Tooltip>
        </Box>
      </Box>

      {/* ── DataTable with Infinite Scroll ─────────────────────────────── */}
      {/* 
        Key points:
        1. key={filterResetKey} - Forces component reset when Fetch button clicked
        2. enableInfiniteScroll={true} - Enables lazy loading
        3. apiFunction={...} - Called to fetch next batch with apiFilters
        4. scrollHeight="600px" - Fixed height for scroll container
        5. enablePagination={false} - Disable traditional pagination
        6. Filters change UI but don't trigger fetch until Fetch button clicked
      */}

      <div className="d-flex align-items-center justify-content-between mb-2 gap-2 flex-wrap">
        <Search
          width={isMobile ? '170px' : '280px'}
          height="36px"
          placeholder="Search by client name, area, city or in charge..."
          debounce={250}
          onSearch={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />

        <div className={`d-flex align-items-center gap-3 flex-shrink-0`}>
          <Button
            variant="contained"
            size="small"
            sx={{
              textTransform: 'capitalize',
              backgroundColor: CSS_PRIMARY,
              '&:hover': {
                backgroundColor: PRIMARY_COLOR_HOVER
              }
            }}
          >
            Total Calls: {filteredData.length}
          </Button>
        </div>

        <div className="d-flex align-items-center gap-2">
          <ExportExcel
            data={dcrList}
            fileName={`DCR_LIST_${new Date().toISOString().slice(0, 10)}.xlsx`}
            sheetName={'DCR_LIST'}
            buttonLabel="Excel"
            exportKeys={headers.filter((h) => h.key !== '_actions').map((h) => h.key)}
            exportHeaders={headers.filter((h) => h.key !== '_actions').map((h) => h.label)}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={15} />}
            onClick={handleAddClient}
            sx={{
              background: 'var(--primary, #82CAFF)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 0.8,
              fontSize: '0.83rem',
              boxShadow: '0 4px 12px rgba(46,204,113,0.28)',
              display: !userAccess.hasAddPermission ? 'none' : 'flex'
            }}
          >
            Add
          </Button>
        </div>
      </div>

      <DataTable
        headers={headers}
        data={filteredData}
        isLoading={isLoading}
        selectable={false}
        emptyMessage={'No Dcr List found in the system.'}
        enablePagination={true}
        zebra={false}
        onRowClick={_editRowHandler}
      />

      <CommonDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={selectedClient ? 'Edit Daily Call Record' : 'Add Daily Call Record'}
        titleIcon={<Plus size={18} />}
        fullScreen={true}
      >
        <AddDcr
          userDetails={userDetails}
          dcrRecord={selectedClient}
          clientsOptions={clients}
          inChargeOptions={inChargeOptions}
          softwareOptions={softwareOptions}
          callTypeOptions={callTypeOptions}
          userNonUserOptions={userNonUserOptions}
          userAccess={userAccess}
          onSuccess={() => {
            handleCloseDialog();
            handleFetch();
          }}
          onCancel={handleCloseDialog}
        />
      </CommonDialog>
    </div>
  );
};

export default DcrList;
